import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CommandPaletteEngine } from "../../command-palette/engine";
import { buildClientSources } from "../../command-palette/sources";
import { findCurrentAppId } from "../../managers/workspace-utils";
import { useWorkspaceNavigation } from "../navigation/useWorkspaceNavigation";
import { useCommand } from "../hooks/useCommand";
import { useRuntime } from "../runtime/RuntimeContext";
import { useWorkspace } from "./WorkspaceProvider";

// Visibility state + open/close/toggle handle for the global Cmd+K palette,
// plus the shared CommandPaletteEngine instance. Mounted once at the
// WorkspaceShell root by WorkspaceManager so any descendant (including the
// Cmd+K hotkey installer and the palette itself) can read or flip the same
// state — and use the same engine — through useCommandPalette().
export interface CommandPaletteContextValue {
    engine: CommandPaletteEngine;
    open: boolean;
    show: () => void;
    hide: () => void;
    toggle: () => void;
}

export const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const boot = useWorkspace();
    const dispatch = useCommand();
    const runtime = useRuntime();
    const { stack } = useWorkspaceNavigation();
    const activeActionKey = stack[0]?.actionKey ?? "";
    // Stable ref so the navigate source can read the current app on every
    // search() without rebuilding when navigation changes.
    const currentAppIdRef = useRef<string | undefined>(undefined);
    currentAppIdRef.current = findCurrentAppId(boot, activeActionKey);
    const getCurrentAppId = useCallback(() => currentAppIdRef.current, []);

    // Single engine instance for the lifetime of the provider. We rebuild the
    // source set whenever (boot, dispatch) changes — tearing down the previous
    // sources by key so we never end up with stale duplicates.
    const engineRef = useRef<CommandPaletteEngine | null>(null);
    if (engineRef.current === null) {
        engineRef.current = new CommandPaletteEngine();
    }
    const engine = engineRef.current;

    useEffect(() => {
        for (const existing of engine.getSources()) {
            engine.unregisterSource(existing.key);
        }
        for (const source of buildClientSources(boot, dispatch, runtime.services.omniSearch, getCurrentAppId)) {
            engine.registerSource(source);
        }
    }, [engine, boot, dispatch, runtime, getCurrentAppId]);

    const show = useCallback(() => setOpen(true), []);
    const hide = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((v) => !v), []);

    const value = useMemo<CommandPaletteContextValue>(
        () => ({ engine, open, show, hide, toggle }),
        [engine, open, show, hide, toggle],
    );

    return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}
